import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { createBar, updateBar } from '../helpers/api';
import { emptyBarForm, musicStyleOptions } from '../helpers/constants';
import { FeatureToggles } from '../components/FeaturePills';

export default function SuggestPage({ userId }) {
  const location = useLocation();
  const editBar = location.state?.bar; // Bar data for editing
  const isEditMode = !!editBar;

  const [formData, setFormData] = useState(editBar || emptyBarForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!userId) {
      navigate('/login');
    }
  }, [userId, navigate]);

  // Fetch neighborhoods list on mount
  useEffect(() => {
    fetch('/data/bairros.json')
      .then(res => res.json())
      .then(data => setNeighborhoods(data))
      .catch(err => console.error('Failed to load neighborhoods:', err));
  }, []);

  // Update form when editBar changes
  useEffect(() => {
    if (editBar) {
      setFormData(editBar);
    }
  }, [editBar]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFeatureToggle = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleMusicStyleToggle = (style) => {
    setFormData(prev => {
      const musicStyles = prev.musicStyles || [];
      if (musicStyles.includes(style)) {
        return { ...prev, musicStyles: musicStyles.filter(s => s !== style) };
      } else {
        return { ...prev, musicStyles: [...musicStyles, style] };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!userId) {
      navigate('/login');
      return;
    }

    if (!formData.name.trim()) {
      setError('Nome do bar é obrigatório');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      let result;
      if (isEditMode) {
        result = await updateBar(userId, editBar.id, formData);
      } else {
        result = await createBar(userId, formData);
      }

      // Redirect to the bar's detail page
      if (result.bar?.key) {
        navigate(`/bar/${result.bar.key}`);
      } else {
        navigate('/');
      }
    } catch (err) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} bar:`, err);
      setError(err.message || `Erro ao ${isEditMode ? 'atualizar' : 'criar'} bar`);
      setSubmitting(false);
    }
  };

  if (!userId) {
    return null; // Will redirect via useEffect
  }

  return (
    <div className="page-container">
      <div className="suggest-form-container">
        <h2>{isEditMode ? 'Editar bar' : 'Sugerir um bar'}</h2>

        <form onSubmit={handleSubmit} className="suggest-form">
          <div className="form-group">
            <label htmlFor="name">Nome do bar *</label>
            <input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Ex: Bar do Zé"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="neighborhood">Bairro / Região</label>
            <input
              id="neighborhood"
              type="text"
              list="neighborhoods-list"
              value={formData.neighborhood}
              onChange={(e) => handleInputChange('neighborhood', e.target.value)}
              placeholder="Ex: Savassi"
              autoComplete="off"
            />
            <datalist id="neighborhoods-list">
              {neighborhoods.map((bairro, idx) => (
                <option key={idx} value={bairro} />
              ))}
            </datalist>
          </div>

          <div className="form-group">
            <label htmlFor="notes">Observações</label>
            <textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Ex: Cheio depois das 22h, melhor chegar cedo"
              rows={3}
            />
          </div>

          <div className="form-group">
            <label>Estilo Musical (opcional)</label>
            <div className="music-styles-grid">
              {musicStyleOptions.map((style) => (
                <button
                  key={style.value}
                  type="button"
                  className={`music-style-chip ${(formData.musicStyles || []).includes(style.value) ? 'selected' : ''}`}
                  onClick={() => handleMusicStyleToggle(style.value)}
                >
                  {style.label}
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Características</label>
            <FeatureToggles values={formData} onChange={handleFeatureToggle} />
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => navigate('/')}
              disabled={submitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={submitting}
            >
              {submitting ? (isEditMode ? 'Salvando...' : 'Enviando...') : (isEditMode ? 'Salvar alterações' : 'Sugerir bar')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
